
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type MarketType = 
  | 'farm_prices'
  | 'distribution_center_prices'
  | 'local_shop_prices'
  | 'sunday_market_prices'
  | 'supermarket_prices'
  | 'ecommerce_prices'
  | 'merkato_prices';

export const MARKET_TYPE_LABELS: Record<MarketType, string> = {
  farm_prices: 'Farm Prices',
  distribution_center_prices: 'Distribution Center',
  local_shop_prices: 'Local Shops',
  sunday_market_prices: 'Sunday Market',
  supermarket_prices: 'Supermarket',
  ecommerce_prices: 'Ecommerce',
  merkato_prices: 'Merkato',
};

export interface BenchmarkEntry {
  id: string;
  product_name: string;
  price: number;
  location: string;
  date: string;
  product_remark: string | null;
  created_at: string;
  created_by: string | null;
}

interface UseBenchmarkEntriesOptions {
  marketType: MarketType;
  productFilter?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function useBenchmarkEntries({ marketType, productFilter, dateFrom, dateTo }: UseBenchmarkEntriesOptions) {
  const queryClient = useQueryClient();

  const queryKey = ['benchmark-entries', marketType, productFilter, dateFrom, dateTo];

  const { data: entries = [], isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      let query = (supabase as any)
        .from(marketType)
        .select('id, product_name, price, location, date, product_remark, created_at, created_by')
        .order('date', { ascending: false })
        .limit(200);

      if (productFilter) {
        query = query.ilike('product_name', `%${productFilter}%`);
      }
      if (dateFrom) {
        query = query.gte('date', dateFrom);
      }
      if (dateTo) {
        query = query.lte('date', dateTo);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as unknown as BenchmarkEntry[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<BenchmarkEntry> }) => {
      const { error } = await (supabase as any)
        .from(marketType)
        .update({
          product_name: updates.product_name,
          price: updates.price,
          location: updates.location,
          date: updates.date,
          product_remark: updates.product_remark,
        })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-entries', marketType] });
      toast.success('Entry updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any)
        .from(marketType)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['benchmark-entries', marketType] });
      toast.success('Entry deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  return {
    entries,
    isLoading,
    updateEntry: updateMutation.mutate,
    deleteEntry: deleteMutation.mutate,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
